import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, Download, Eye, Filter, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface IssuedCertificate {
  id: string;
  user_id: string;
  course_id: string;
  certificate_number: string;
  issue_date: string;
  status: string;
  certificate_data: any;
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
    school_name?: string;
  };
  courses: {
    title: string;
    duration_hours: number;
  };
  certificate_templates: {
    name: string;
  };
}

export function IssuedCertificates() {
  const [certificates, setCertificates] = useState<IssuedCertificate[]>([]);
  const [filteredCertificates, setFilteredCertificates] = useState<IssuedCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadCertificates();
  }, []);

  useEffect(() => {
    filterCertificates();
  }, [certificates, searchTerm, statusFilter]);

  const loadCertificates = async () => {
    try {
      const { data, error } = await supabase
        .from('issued_certificates')
        .select(`
          *,
          profiles!issued_certificates_user_id_fkey(first_name, last_name, email, school_name),
          courses(title, duration_hours),
          certificate_templates!issued_certificates_template_id_fkey(name)
        `)
        .order('issue_date', { ascending: false });

      if (error) throw error;
      setCertificates(data || []);
    } catch (error) {
      console.error('Error loading certificates:', error);
      toast.error('Failed to load issued certificates');
    } finally {
      setLoading(false);
    }
  };

  const filterCertificates = () => {
    let filtered = certificates;

    if (searchTerm) {
      filtered = filtered.filter(cert => 
        cert.profiles?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.profiles?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.courses?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(cert => cert.status === statusFilter);
    }

    setFilteredCertificates(filtered);
  };

  const handleDownload = (certificate: IssuedCertificate) => {
    // Placeholder for certificate download functionality
    toast.info('Certificate download functionality will be implemented');
  };

  const handlePreview = (certificate: IssuedCertificate) => {
    // Placeholder for certificate preview functionality
    toast.info('Certificate preview functionality will be implemented');
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading certificates...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Issued Certificates</h3>
        <p className="text-sm text-muted-foreground">
          View and manage all issued certificates
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, course, or certificate number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="revoked">Revoked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Certificate List */}
      <div className="space-y-4">
        {filteredCertificates.map((certificate) => (
          <Card key={certificate.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback>
                      {certificate.profiles?.first_name?.[0]}{certificate.profiles?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">
                        {certificate.profiles?.first_name} {certificate.profiles?.last_name}
                      </h4>
                      <Badge variant={certificate.status === 'active' ? 'default' : 'destructive'}>
                        {certificate.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{certificate.profiles?.email}</p>
                    {certificate.profiles?.school_name && (
                      <p className="text-sm text-muted-foreground">{certificate.profiles.school_name}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Course: {certificate.courses?.title}</span>
                      <span>•</span>
                      <span>{certificate.courses?.duration_hours} hours</span>
                      <span>•</span>
                      <span>#{certificate.certificate_number}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Issued: {format(new Date(certificate.issue_date), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => handlePreview(certificate)}>
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDownload(certificate)}>
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCertificates.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">
              {searchTerm || statusFilter !== 'all' 
                ? 'No certificates match your search criteria.' 
                : 'No certificates have been issued yet.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Total: {certificates.length} certificates</span>
            <span>Showing: {filteredCertificates.length} certificates</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}